import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";

const LAST_UPDATED = "July 12, 2026";

export const Route = createFileRoute("/legal/returns")({
  head: () => ({
    meta: [
      { title: "Return Policy — Wigs by Flora" },
      { name: "description", content: "Strict 7-day return policy for unworn, unaltered wigs. 20% restocking fee applies." },
      { property: "og:title", content: "Return Policy — Wigs by Flora" },
      { property: "og:description", content: "Strict 7-day return policy for unworn, unaltered wigs. 20% restocking fee applies." },
      { property: "og:url", content: "https://europeanwigluxury.com/legal/returns" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/legal/returns" }],
  }),
  component: ReturnsPage,
});

function ReturnsPage() {
  const [ack, setAck] = useState(false);
  return (
    <article className="container-luxe max-w-3xl py-16 md:py-24">
      <p className="eyebrow">Legal</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl">Return Policy</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        Last updated: {LAST_UPDATED}
      </p>

      <div className="prose-luxe mt-10 space-y-8 text-base leading-relaxed text-foreground/85">
        <section className="rounded-md border border-bronze/40 bg-bronze/5 p-6">
          <p className="font-display text-lg">Please read carefully before purchasing.</p>
          <p className="mt-3">Wigs are worn against the scalp. For hygiene and quality reasons we operate a <strong>strict, limited return policy</strong>. By placing an order you confirm you have read and accepted the terms below.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl">7-day return window</h2>
          <p className="mt-3">Returns must be <strong>requested within 7 days of the purchase date</strong>. After 7 days, all sales are final. To start a return, email <a className="text-bronze underline" href="mailto:care@europeanwigluxury.com">care@europeanwigluxury.com</a> with your order number.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl">20% restocking fee</h2>
          <p className="mt-3">All approved returns are subject to a <strong>20% restocking fee</strong>, deducted from your refund. Original shipping is non-refundable. Return shipping is the customer's responsibility.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl">Returns will be refused if the wig has been:</h2>
          <ul className="mt-3 space-y-2">
            <li>· Altered, cut, colored, washed, or restyled in any way</li>
            <li>· Exposed to perfume, cologne, hairspray, or any fragrance</li>
            <li>· Exposed to cigarette, cigar, vape, or any smoke</li>
            <li>· Torn, ripped, tangled, or physically damaged</li>
            <li>· Carrying any odor (food, pet, body, cleaning products, etc.)</li>
            <li>· Returned without original packaging, hairnet, or tags</li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">Every returned wig is inspected on arrival. If it fails inspection, the return is refused and the wig is shipped back to you at your expense; no refund is issued.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl">No custom orders</h2>
          <p className="mt-3">We do not accept custom-color, custom-cut, or made-to-measure orders. Every piece is sold as-shown.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl">How to try on safely</h2>
          <ul className="mt-3 space-y-2">
            <li>· Try on over a clean wig cap, on freshly washed skin, in a fragrance-free room</li>
            <li>· Do not brush, cut, wash, or style</li>
            <li>· Keep all original packaging and the hairnet in place until you decide</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl">Refunds</h2>
          <p className="mt-3">Once we receive and approve your return, we refund the original payment method (minus the 20% restocking fee and original shipping) within 5–7 business days.</p>
        </section>

        <section className="rounded-md border border-border/70 bg-secondary/40 p-6">
          <div className="flex items-start gap-3">
            <Checkbox
              id="return-ack"
              checked={ack}
              onCheckedChange={(v) => setAck(v === true)}
              className="mt-1"
            />
            <label htmlFor="return-ack" className="cursor-pointer text-sm leading-relaxed">
              <span className="font-medium text-foreground">I have read and agree to the Return Policy.</span>
              <span className="block mt-1 text-muted-foreground">
                I understand returns must be requested within 7 days, that a 20% restocking fee applies, and that returns will be refused if the wig has been altered, cut, worn with fragrance, exposed to smoke, torn, or carries any odor.
              </span>
            </label>
          </div>
          {ack && (
            <p className="mt-4 text-sm text-bronze">✔ Thank you — your acknowledgment has been recorded for this session.</p>
          )}
        </section>
      </div>
    </article>
  );
}
